import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Store, MapPin, Phone, Mail, Clock, Truck, Save, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { toast } from 'sonner';
import { useRestaurant } from '../../context/RestaurantContext';

const CUISINE_OPTIONS = [
  'British', 'Italian', 'Chinese', 'Indian', 'Mexican', 'Thai', 'Japanese', 
  'American', 'French', 'Mediterranean', 'Middle Eastern', 'Vietnamese',
  'Korean', 'Greek', 'Spanish', 'Caribbean', 'African', 'Turkish', 'Other'
];

export default function CreateRestaurantForm() {
  const { t } = useTranslation();
  const { createRestaurant } = useRestaurant();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '',
    description: '',
    cuisine_type: [],
    address: '',
    city: '',
    postal_code: '',
    phone: '',
    email: '',
    delivery_radius_km: 5,
    minimum_order: 10,
    delivery_fee: 2.99,
  });

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const toggleCuisine = (cuisine) => {
    setForm(prev => ({
      ...prev,
      cuisine_type: prev.cuisine_type.includes(cuisine)
        ? prev.cuisine_type.filter(c => c !== cuisine)
        : [...prev.cuisine_type, cuisine]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!form.name.trim()) {
      toast.error(t('createRestaurant.errorName'));
      return;
    }
    if (!form.address.trim()) {
      toast.error(t('createRestaurant.errorAddress'));
      return;
    }
    if (!form.city.trim()) {
      toast.error(t('createRestaurant.errorCity'));
      return;
    }
    if (form.cuisine_type.length === 0) {
      toast.error(t('createRestaurant.errorCuisine'));
      return;
    }

    setSaving(true);
    try {
      await createRestaurant(form);
      toast.success(t('createRestaurant.success'));
    } catch (err) {
      // Handle validation error objects
      const detail = err.response?.data?.detail;
      let errorMessage = t('createRestaurant.errorFailed');
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail.map(d => d.msg || d).join(', ');
      }
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="create-restaurant-form">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Store className="w-8 h-8 text-orange-500" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900">{t('createRestaurant.title')}</h1>
        <p className="text-slate-500 mt-2">
          {t('createRestaurant.subtitle')}
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Store className="w-5 h-5 text-orange-500" />
              {t('createRestaurant.basicInfo')}
            </CardTitle>
            <CardDescription>{t('createRestaurant.basicInfoDesc')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">{t('createRestaurant.restaurantName')} *</Label>
              <Input
                id="name"
                placeholder={t('createRestaurant.restaurantNamePlaceholder')}
                value={form.name}
                onChange={(e) => handleChange('name', e.target.value)}
                data-testid="restaurant-name-input"
              />
            </div>

            <div>
              <Label htmlFor="description">{t('createRestaurant.description')}</Label>
              <Textarea
                id="description"
                placeholder={t('createRestaurant.descriptionPlaceholder')}
                value={form.description}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={3}
                data-testid="restaurant-description-input"
              />
            </div>

            <div>
              <Label>{t('createRestaurant.cuisineType')} *</Label>
              <p className="text-sm text-slate-500 mb-2">{t('createRestaurant.selectAll')}</p>
              <div className="flex flex-wrap gap-2">
                {CUISINE_OPTIONS.map(cuisine => (
                  <button
                    key={cuisine}
                    type="button"
                    onClick={() => toggleCuisine(cuisine)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      form.cuisine_type.includes(cuisine)
                        ? 'bg-orange-500 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    data-testid={`cuisine-${cuisine.toLowerCase()}`}
                  >
                    {cuisine}
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-orange-500" />
              {t('createRestaurant.locationContact')}
            </CardTitle>
            <CardDescription>{t('createRestaurant.locationContactDesc')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="address">{t('createRestaurant.streetAddress')} *</Label>
              <Input
                id="address"
                placeholder={t('createRestaurant.streetAddressPlaceholder')}
                value={form.address}
                onChange={(e) => handleChange('address', e.target.value)}
                data-testid="restaurant-address-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="city">{t('createRestaurant.city')} *</Label>
                <Input
                  id="city"
                  placeholder={t('createRestaurant.cityPlaceholder')}
                  value={form.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  data-testid="restaurant-city-input"
                />
              </div>
              <div>
                <Label htmlFor="postal_code">{t('createRestaurant.postalCode')}</Label>
                <Input
                  id="postal_code"
                  placeholder={t('createRestaurant.postalCodePlaceholder')}
                  value={form.postal_code}
                  onChange={(e) => handleChange('postal_code', e.target.value)}
                  data-testid="restaurant-postal-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="phone">{t('createRestaurant.phone')}</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder={t('createRestaurant.phonePlaceholder')}
                  value={form.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  data-testid="restaurant-phone-input"
                />
              </div>
              <div>
                <Label htmlFor="email">{t('createRestaurant.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t('createRestaurant.emailPlaceholder')}
                  value={form.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  data-testid="restaurant-email-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-orange-500" />
              {t('createRestaurant.deliverySettings')}
            </CardTitle>
            <CardDescription>{t('createRestaurant.deliverySettingsDesc')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="delivery_radius">{t('createRestaurant.deliveryRadius')}</Label>
                <Input
                  id="delivery_radius"
                  type="number"
                  min="1"
                  max="50"
                  value={form.delivery_radius_km}
                  onChange={(e) => handleChange('delivery_radius_km', parseFloat(e.target.value) || 5)}
                  data-testid="delivery-radius-input"
                />
              </div>
              <div>
                <Label htmlFor="minimum_order">{t('createRestaurant.minimumOrder')}</Label>
                <Input
                  id="minimum_order"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.minimum_order}
                  onChange={(e) => handleChange('minimum_order', parseFloat(e.target.value) || 0)}
                  data-testid="minimum-order-input"
                />
              </div>
              <div>
                <Label htmlFor="delivery_fee">{t('createRestaurant.deliveryFee')}</Label>
                <Input
                  id="delivery_fee"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.delivery_fee}
                  onChange={(e) => handleChange('delivery_fee', parseFloat(e.target.value) || 0)}
                  data-testid="delivery-fee-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-4">
          <Button
            type="submit"
            className="btn-primary px-8"
            disabled={saving}
            data-testid="create-restaurant-submit"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t('createRestaurant.creating')}
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {t('createRestaurant.createButton')}
              </>
            )}
          </Button>
        </div>
      </form>

      <div className="mt-8 p-4 bg-blue-50 rounded-xl border border-blue-100">
        <h3 className="font-semibold text-blue-900 mb-2">{t('createRestaurant.whatHappensNext')}</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• {t('createRestaurant.nextStep1')}</li>
          <li>• {t('createRestaurant.nextStep2')}</li>
          <li>• {t('createRestaurant.nextStep3')}</li>
          <li>• {t('createRestaurant.nextStep4')}</li>
          <li>• {t('createRestaurant.nextStep5')}</li>
        </ul>
      </div>
    </div>
  );
}
